import { useEffect, useRef } from "react"
import { FormControlLabel, ListItemText, Radio, Stack, Typography } from "@mui/material"
import { BoundAutocomplete, BoundRadioGroup, BoundTextField } from "lib/@components/binding/bound-components"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { If } from "lib/@components/switch"
import { skillMaps } from "routes/skill-mapper/controller/skill-maps"
import { useUniqueNameValidator } from "../use-unique-name-validation"

export function CreateSkillMapFormBody() {
    const { target } = useBoundContext()
    const [name, setName] = useBoundValue("name")
    const result = skillMaps.useResults({ skip: 0, take: 1000, status: "active" })
    const existingMaps = (result?.items || []).map(toSkillMapOption)
    // Separate status:"all" load so the duplicate-name check matches the server (which
    // counts archived maps too); the "active" list above only feeds the copy-from picker.
    const allMaps = skillMaps.useResults({ skip: 0, take: 1000, status: "all" })
    const validateName = useUniqueNameValidator(allMaps?.items, "skill mapping")
    const hasSetDefault = useRef(false)
    useEffect(prePopulateName, [name, result, setName])

    return (
        <Stack spacing={2} sx={{ mt: 1 }}>
            <BoundTextField field="name" label="Name" yup={validateName} placeholder="Enter a name..." />
            <BoundTextField
                field="description"
                label="Description"
                placeholder="Optional description..."
                multiline
                minRows={2}
                maxRows={4}
            />
            <BoundRadioGroup field="startingPoint" sideEffects={true}>
                <Stack spacing={1}>
                    <FormControlLabel
                        value="defaults"
                        control={<Radio />}
                        label={
                            <ListItemText
                                primary={
                                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                        Create from SFG20 default
                                    </Typography>
                                }
                                secondary="Pre-populate with standard SFG20 skill-to-trade mappings"
                            />
                        }
                    />
                    <FormControlLabel
                        value="scratch"
                        control={<Radio />}
                        label={
                            <ListItemText
                                primary={
                                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                        Create from scratch
                                    </Typography>
                                }
                                secondary="All skills from your regimes with no trade assignments"
                            />
                        }
                    />
                    <If value={existingMaps.length > 0}>
                        <FormControlLabel
                            value="existing"
                            control={<Radio />}
                            label={
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                            Copy from existing
                                        </Typography>
                                    }
                                    secondary="Duplicate an existing skill map as a starting point"
                                />
                            }
                        />
                    </If>
                </Stack>
            </BoundRadioGroup>
            <If value={target.startingPoint === "existing"}>
                <BoundAutocomplete
                    field="sourceSkillMapId"
                    label="Skill Map"
                    options={existingMaps}
                    sideEffects={true}
                />
            </If>
        </Stack>
    )

    function prePopulateName() {
        if (hasSetDefault.current || !result || name) return
        hasSetDefault.current = true
        const count = (result?.items || []).length
        setName(`Skill Mapping ${count + 1}`)
    }
}

function toSkillMapOption(map) {
    return { value: map.id, label: map.name }
}
